import { Dispatch, SetStateAction, useEffect } from "react";
import { useQuery } from "react-query";

import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRowsProp,
} from "@mui/x-data-grid";
import { FeatureCode } from "@wallet-manager/node-types";
import EnumRequestStatus from "@wallet-manager/node-types/dist/types/ledger/enums/LedgerRequestStatus";

import { getCrossMerchantTransferRequest } from "../../../api/merchant";
import { OpApprove, OpRedirect } from "../../../assets/icons";
import { CustomPagination } from "../../../components/CustomPagination";
import LoadingDialog from "../../../components/LoadingDialog";
import { Box } from "../../../components/MuiGenerals";
import { NoRowsOverlay } from "../../../components/NoRowsOverlay";
import OpIconButton from "../../../components/OpIconButton";
import { usePermission, useTranslation } from "../../../hooks";
import useColumnHide, {
  ColumnHiddenMessage,
  getColumnHideConfig,
} from "../../../hooks/useColumnHide";
import { selectCrossMerchant } from "../../../reducer/crossMerchantSlice";
import { useAppSelector } from "../../../reducer/hooks";
import { dataGridDefaults } from "../../../utils/constant";
import { useGenGridCol } from "../../../utils/HelperComp";
import { useZusDialogStore } from "../../../zustand/store";
import ApprovalDialog from "../ApprovalDialog";
import ApprovalProgressDialog from "../ApprovalProgressDialog";
import { useZusParams } from "./CrossMerchantTransferRequest";

const translatePrefix = "crossMerchantTransferRequest";
const Pkey = FeatureCode.FeaturesLedger.CrossMerchantTransferRequest;
const PAGE = "CrossMerchantTransferRequest";
const api = {
  getCrossMerchantTransferRequest,
};
export default function CrossMerchantTransferRequestList(props: {
  listMapping: (
    which: "key" | "name",
    array: any[],
    config?: {
      omitKeys: string[];
      t: any;
      te: any;
      merchantMapping: Record<string, string | number>;
    }
  ) => any[][];
  setCount: Dispatch<SetStateAction<number>>;
}) {
  const { t, te } = useTranslation(translatePrefix);
  const { hasPermission } = usePermission();

  const zusParams = useZusParams();
  const zusDialog = useZusDialogStore();
  const { merchantMapping } = useAppSelector(selectCrossMerchant);
  const { listMapping, setCount } = props;
  const listRes = useQuery({
    queryKey: [
      "crossMerchantTransferRequest",
      zusParams.body,
      zusParams.refetchCounter,
    ],
    queryFn: () => api.getCrossMerchantTransferRequest(zusParams.body),
    // cacheTime: 0,
  });

  const { rows = [], count = 0 } =
    (listRes.data as any as { rows: any[]; count: number }) || {};
  const content: GridRowsProp = listMapping("key", rows, {
    omitKeys: [],
    t,
    te,
    merchantMapping,
  });

  useEffect(() => setCount(count), [listRes]);

  const ApprovalProgress = (params: GridCellParams) => {
    const { id: rowId, row } = params;
    const rawRow = rows[Number(rowId)];
    const { id, status } = rawRow || {};
    const { approval_progress, last_modified_time } = row;
    const onClick = () => {
      zusDialog.open("approvalDialog", {
        approval_progress,
        id,
        status,
        last_modified_time,
        page: PAGE,
      });
    };
    if (!approval_progress) return <></>;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "40%",
        }}
      >
        <span>{approval_progress}</span>
        <OpIconButton
          title={""}
          url={OpRedirect}
          onClick={onClick}
          size="1rem"
        />
      </Box>
    );
  };
  const ApproveBtn = (params: GridCellParams) => {
    const { id, row } = params;
    const {
      merchant_from,
      merchant_to,
      client_id_from,
      client_id_to,
      chain_name_from,
      chain_name_to,
      currency_from,
      currency_to,
      liquidity_provider,
      exchange_rate,
      transaction_amount_from,
      transaction_amount_to,
      merchant_lp,
    } = row;

    const rawData = rows[Number(id)];
    const {
      id: request_id,
      approvals: approval_completed,
      created_by,
    } = rawData || {};
    const apiParams = { request_id, approval_completed, created_by };
    const onClick = () => {
      zusDialog.open("operationDialog", {
        merchant_from,
        merchant_to,
        client_id_from,
        client_id_to,
        chain_name_from,
        chain_name_to,
        currency_from,
        currency_to,
        merchant_lp,
        liquidity_provider,
        transaction_amount_from,
        exchange_rate,
        transaction_amount_to,
        apiParams,
        page: PAGE,
      });
    };
    return (
      <OpIconButton
        title={t("approveBtn")}
        url={OpApprove}
        isDisabled={rawData.status !== EnumRequestStatus.Pending}
        size="1.9rem"
        onClick={onClick}
      />
    );
  };

  const hasApprovePermission = hasPermission(Pkey.Approve);
  const hasRejectPermission = hasPermission(Pkey.Reject);

  const shouldHideOperationColumn =
    !hasApprovePermission && !hasRejectPermission;

  const columns: GridColDef[] = [
    useGenGridCol("operation", t("operation"), {
      minWidth: 70,
      renderCell: ApproveBtn,
    }),
    useGenGridCol("merchant_order_id", t("merchant_order_id"), {
      minWidth: 150,
    }),
    useGenGridCol("approval_progress", t("approval_progress"), {
      minWidth: 130,
      renderCell: ApprovalProgress,
    }),
    useGenGridCol("creator_type", t("creator_type"), { minWidth: 100 }),
    useGenGridCol("merchant_from", t("merchant_from"), {
      minWidth: 100,
    }),
    useGenGridCol("client_id_from", t("client_id_from"), {
      minWidth: 100,
    }),
    useGenGridCol("chain_name_from", t("chain_name_from"), { minWidth: 100 }),
    useGenGridCol("currency_from", t("currency_from"), {
      minWidth: 100,
    }),
    useGenGridCol("merchant_to", t("merchant_to"), {
      minWidth: 100,
    }),
    useGenGridCol("client_id_to", t("client_id_to"), {
      minWidth: 100,
    }),
    useGenGridCol("chain_name_to", t("chain_name_to"), { minWidth: 100 }),
    useGenGridCol("currency_to", t("currency_to"), {
      minWidth: 100,
    }),
    useGenGridCol("merchant_lp", t("merchant_lp"), {
      minWidth: 100,
    }),
    useGenGridCol("liquidity_provider", t("liquidity_provider"), {
      minWidth: 100,
    }),
    useGenGridCol("exchange_rate", t("exchange_rate"), { minWidth: 100 }),
    useGenGridCol("transaction_amount_from", t("transaction_amount_from"), {
      minWidth: 100,
    }),
    useGenGridCol("transaction_amount_to", t("transaction_amount_to"), {
      minWidth: 100,
    }),
    useGenGridCol("request_status", t("request_status"), {
      minWidth: 100,
    }),
    useGenGridCol("created_by", t("created_by"), { minWidth: 100 }),
    useGenGridCol("creation_time", t("creation_time"), { minWidth: 150 }),
    useGenGridCol("last_modified_time", t("last_modified_time"), {
      minWidth: 150,
    }),
  ].filter((col) => {
    if (col.field === "operation") {
      return !shouldHideOperationColumn;
    }

    return true;
  });

  useEffect(() => () => zusParams.clear(), []);
  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    columns,
    getColumnHideConfig()
  );
  if (listRes.isLoading) return <LoadingDialog forceOpen={true} />;
  return (
    <>
      <ApprovalDialog />
      <ApprovalProgressDialog />
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={content}
        rowCount={count}
        columns={columns}
        page={zusParams.body.page}
        onPageChange={zusParams.setPage}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords: count },
        }}
      />
    </>
  );
}
